import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_LONG_MS } from "@/lib/query-constants";
import { buildQueryTuningFromForm } from "../lib/persona-opportunity-form";
import type {
  CreatePersonaSetFormValues,
  CreatePersonaSetSubmit,
  PersonaOpportunityQueryTuning,
} from "../types/persona-opportunities.types";
import type { CreateSetFormValues } from "@/features/opportunities/components/sets/CreateSetDialog/CreateSetDialog";

// ─── Types ──────────────────────────────────────────────────────────

export type PersonaOpportunitySetStatus = "GENERATING" | "READY" | "FAILED";
export type PersonaOpportunitySetType = "PERSONA" | "COMPETITOR" | "USER_GENERATED" | "POST_FLIGHT";

/** Opportunity row occupancy — matches `PersonaOpportunityStatus` in `@nexus/db`. */
export type PersonaOpportunityStatus =
  | "NEW"
  | "SCRIPTS_GENERATED"
  | "BRIEFS_GENERATED"
  | "SCRIPTS_AND_BRIEFS_GENERATED"
  | "SENT_TO_VENDOR";

/** Persona selected when the set was created. Titles stay even if the persona is later hidden. */
export interface PersonaOpportunitySetPersonaTitle {
  id: string;
  title: string;
}

/** `persona_opportunity_sets.config` snapshot taken when the set was created. */
export interface PersonaOpportunitySetConfig {
  type?: PersonaOpportunitySetType;
  industry?: string | null;
  benchmarkIndustry?: string | null;
  myBrand?: string | null;
  mainCompetitor?: string | null;
  industryCompetitors?: string[] | null;
  adPlatforms?: string[] | null;
  candidatePoolSize?: number | null;
  anglesReturned?: number | null;
  maxAnglesPerFeature?: number | null;
  minBrandsPerFeature?: number | null;
  stickDays?: number | null;
}

/** Set row as serialized by the BFF — Prisma dates arrive as ISO strings. */
export interface PersonaOpportunitySetRow {
  id: string;
  tenantId: string;
  name: string;
  status: PersonaOpportunitySetStatus;
  type?: PersonaOpportunitySetType;
  customerPartition: string | null;
  startDate: string | null;
  endDate: string | null;
  config: PersonaOpportunitySetConfig | null;
  /** ISO timestamp. Absent on rows created before the column was read by the client. */
  generationStartedAt?: string | null;
  generationCompletedAt?: string | null;
  /** Product names from the set-product join. Empty when the set stored none. */
  products?: string[] | null;
  /** Personas selected at creation. Empty for competitor sets and for rows that predate the field. */
  personas?: PersonaOpportunitySetPersonaTitle[] | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Present on list responses (`_count.opportunities`). Detail responses omit it —
   * sum the persona opportunity arrays instead.
   */
  opportunityCount?: number;
  /** Non-deleted persona scripts on the set — list + detail. */
  scriptCount?: number;
  /** Non-deleted persona briefs on the set — list + detail. */
  briefCount?: number;
  /** Live scripts and briefs on the set that have a marketplace order line. List rows. */
  sentCount?: number;
}

export interface PersonaOpportunitySetListPage {
  items: PersonaOpportunitySetRow[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** PRD §10 KPI snapshot denormalized onto each opportunity at generation time. */
export interface PersonaOpportunityKpis {
  drivingFeatureScore: number;
  creativesCount: number;
  competitorsRunningCount: number;
  stickRatePct: number;
  duplicatedPct: number;
  myInvestmentSharePct: number;
  marketInvestmentSharePct: number;
  investmentIndex?: number | null;
}

export interface PersonaOpportunitySupportingAd {
  id: string;
  opportunityId: string;
  hash: string | null;
  competitor: string | null;
  url: string | null;
  thumbnailUrl: string | null;
  /** Competitor brand logo from BQ `brands_metadata.profile_picture_url`. */
  profilePictureUrl?: string | null;
  daysLive: number | null;
  duplications: number | null;
  rank: number;
  createdAt: string;
}

export interface PersonaOpportunityRow {
  id: string;
  setId: string;
  personaId: string;
  status: PersonaOpportunityStatus;
  angle: string;
  painPoint: string | null;
  motivation: string | null;
  /** BQ `driven_by` — null on rows generated before the column existed. */
  drivenBy: "painpoint" | "motivation" | null;
  /** Competitor source dataset; omitted by payloads before it shipped. */
  dataset?: string | null;
  rank: number;
  /** Synthetic "Your Scripts" angle — hidden from the Opportunities tab. */
  isCustom?: boolean;
  kpis: PersonaOpportunityKpis;
  createdAt: string;
  updatedAt: string;
  supportingAd: PersonaOpportunitySupportingAd | null;
  provenanceCaption: string | null;
  topicDescription?: string | null;
  feature?: string | null;
  /** Post-Flight winning-ad metrics. Absent on other set types. */
  metadata?: unknown;
}

export interface PersonaOpportunitySetPersonaSection {
  personaId: string;
  title: string;
  who: string | null;
  pains: string[];
  motivations: string[];
  /** Omitted by older set-detail payloads before portraits shipped. */
  personalityKey?: string;
  avatarUrl?: string | null;
  /** ISO timestamp once BigQuery stopped returning this persona; the set still shows it. */
  staledAt: string | null;
  opportunities: PersonaOpportunityRow[];
}

export interface PersonaOpportunitySetDetail {
  /** `competitorsTotal`, `scriptCount`, and `briefCount` are derived at read time. */
  set: PersonaOpportunitySetRow & {
    competitorsTotal: number;
    scriptCount?: number;
    briefCount?: number;
  };
  personas: PersonaOpportunitySetPersonaSection[];
}

// ─── Read Endpoints ─────────────────────────────────────────────────

export interface FetchPersonaOpportunitySetsParams {
  cursor?: string;
  limit?: number;
}

export async function fetchPersonaOpportunitySets(
  params: FetchPersonaOpportunitySetsParams = {},
): Promise<PersonaOpportunitySetListPage> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.size > 0 ? `?${qs}` : "";

  const { data } = await authFetch<PersonaOpportunitySetListPage>(
    `/persona-opportunity-sets${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export async function fetchPersonaOpportunitySetDetail(
  id: string,
): Promise<PersonaOpportunitySetDetail> {
  const { data } = await authFetch<PersonaOpportunitySetDetail>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}`,
    // Detail fans out to personas, opportunities, and supporting ads in one call.
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

// ─── Generate (Amber Path) ──────────────────────────────────────────

export interface GeneratePersonaOpportunitySetPersonaRequest {
  type?: "PERSONA";
  tenantId: string;
  idempotencyKey: string;
  name: string;
  personaIds: string[];
  industry: string;
  industryCompetitors: string[];
  startDate: string;
  endDate: string;
  /** v1 accepts a single platform — BQ filters on one `@platform`. */
  adPlatforms?: string[];
  advanced?: PersonaOpportunityQueryTuning;
}

export interface GeneratePersonaOpportunitySetCompetitorRequest {
  type: "COMPETITOR";
  tenantId: string;
  idempotencyKey: string;
  name: string;
  industry: string;
  startDate: string;
  endDate: string;
  benchmarkIndustry?: string;
  myBrand?: string;
  mainCompetitor?: string;
  industryCompetitors?: string[];
  products?: string[];
  adPlatforms?: string[];
  strategicPriorities?: { focusOn?: string[]; avoid?: string[] };
  brandVoice?: { alwaysSay?: string; neverSay?: string };
  scriptGuidelines?: string;
  minimalGeneration?: boolean;
}

export type GeneratePersonaOpportunitySetRequest =
  | GeneratePersonaOpportunitySetPersonaRequest
  | GeneratePersonaOpportunitySetCompetitorRequest;

function hasTuningOverrides(form: CreatePersonaSetFormValues): boolean {
  return form.showAdvanced;
}

function optionalTrimmed(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function buildGeneratePersonaOpportunitySetRequest(
  submit: CreatePersonaSetSubmit,
  context: { tenantId: string; idempotencyKey: string; canUseQueryTuning: boolean },
): GeneratePersonaOpportunitySetRequest {
  if (submit.type === "COMPETITOR") {
    return buildGenerateCompetitorOpportunitySetRequest(submit.values, context);
  }
  return buildGeneratePersonaDrivenOpportunitySetRequest(submit.values, context);
}

function buildGeneratePersonaDrivenOpportunitySetRequest(
  form: CreatePersonaSetFormValues,
  context: { tenantId: string; idempotencyKey: string; canUseQueryTuning: boolean },
): GeneratePersonaOpportunitySetPersonaRequest {
  const adPlatform = form.adPlatform.trim();
  const includeAdvanced = context.canUseQueryTuning && hasTuningOverrides(form);

  return {
    tenantId: context.tenantId,
    idempotencyKey: context.idempotencyKey,
    name: form.name.trim(),
    personaIds: form.personaIds,
    industry: form.industry,
    industryCompetitors: form.industryCompetitors,
    startDate: form.startDate,
    endDate: form.endDate,
    ...(adPlatform ? { adPlatforms: [adPlatform] } : {}),
    ...(includeAdvanced ? { advanced: buildQueryTuningFromForm(form) } : {}),
  };
}

function buildGenerateCompetitorOpportunitySetRequest(
  form: CreateSetFormValues,
  context: { tenantId: string; idempotencyKey: string },
): GeneratePersonaOpportunitySetCompetitorRequest {
  const myBrand = optionalTrimmed(form.brand);
  const mainCompetitor = optionalTrimmed(form.mainCompetitor);
  const benchmarkIndustry = optionalTrimmed(form.benchmarkIndustry);
  const adPlatform = optionalTrimmed(form.adPlatforms);
  const scriptGuidelines = optionalTrimmed(form.scriptGuidelines);
  const alwaysSay = optionalTrimmed(form.brandVoice.alwaysSay);
  const neverSay = optionalTrimmed(form.brandVoice.neverSay);
  const focusOn = form.strategicPriorities.focusOn.filter((item) => item.trim().length > 0);
  const avoid = form.strategicPriorities.avoid.filter((item) => item.trim().length > 0);

  return {
    type: "COMPETITOR",
    tenantId: context.tenantId,
    idempotencyKey: context.idempotencyKey,
    name: form.name.trim(),
    industry: form.industry,
    startDate: form.startDate,
    endDate: form.endDate,
    ...(benchmarkIndustry ? { benchmarkIndustry } : {}),
    ...(myBrand ? { myBrand } : {}),
    ...(mainCompetitor ? { mainCompetitor } : {}),
    ...(form.industryCompetitors.length > 0
      ? { industryCompetitors: form.industryCompetitors }
      : {}),
    ...(form.products.length > 0 ? { products: form.products } : {}),
    ...(adPlatform ? { adPlatforms: [adPlatform] } : {}),
    ...(focusOn.length > 0 || avoid.length > 0
      ? { strategicPriorities: { ...(focusOn.length > 0 ? { focusOn } : {}), ...(avoid.length > 0 ? { avoid } : {}) } }
      : {}),
    ...(alwaysSay || neverSay
      ? { brandVoice: { ...(alwaysSay ? { alwaysSay } : {}), ...(neverSay ? { neverSay } : {}) } }
      : {}),
    ...(scriptGuidelines ? { scriptGuidelines } : {}),
    ...(form.minimalGeneration ? { minimalGeneration: true } : {}),
  };
}

/** Request-tenant own brand slug for competitor exclusion in the create dialog. */
export interface PersonaOwnBrand {
  slug: string | null;
}

export async function fetchPersonaOwnBrand(): Promise<PersonaOwnBrand> {
  const { data } = await authFetch<PersonaOwnBrand>("/persona-opportunities/own-brand");
  return data;
}

/** POSTs the generate request and returns the Amber correlation id from the 202. */
export async function generatePersonaOpportunitySet(
  body: GeneratePersonaOpportunitySetRequest,
): Promise<{ correlationId: string; setId?: string }> {
  const { correlationId, data } = await authFetch<{ setId?: string }>(
    "/persona-opportunity-sets/generate",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Generate response missing correlationId");
  }
  const setId = data?.setId?.trim();
  return { correlationId, ...(setId ? { setId } : {}) };
}

/** Creates a ready user-generated set. The response is the set id, not a correlation id. */
export async function createUserGeneratedPersonaOpportunitySet(
  name: string,
): Promise<{ id: string; name: string }> {
  const { data } = await authFetch<{ id: string; name: string }>(
    "/persona-opportunity-sets/user-generated",
    {
      method: "POST",
      body: JSON.stringify({ name }),
    },
  );
  return data;
}

// ─── Rename ─────────────────────────────────────────────────────────

export async function renamePersonaOpportunitySet(
  id: string,
  name: string,
): Promise<PersonaOpportunitySetRow> {
  const { data } = await authFetch<PersonaOpportunitySetRow>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify({ name }) },
  );
  return data;
}

// ─── Delete / retry / regenerate ────────────────────────────────────

export async function deletePersonaOpportunitySet(id: string): Promise<void> {
  await authFetch(`/persona-opportunity-sets/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: JSON.stringify({}),
    rawResponse: true,
  });
}

export interface RetryPersonaOpportunitySetRequest {
  tenantId: string;
  idempotencyKey: string;
}

export async function retryPersonaOpportunitySet(
  id: string,
  body: RetryPersonaOpportunitySetRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}/retry`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Retry response missing correlationId");
  }
  return { correlationId };
}

export type RegeneratePersonaOpportunitySetMode = "overwrite" | "new_set";

export interface RegeneratePersonaOpportunitySetRequest {
  tenantId: string;
  idempotencyKey: string;
  mode: RegeneratePersonaOpportunitySetMode;
  /** Required when `mode === "new_set"`. */
  name?: string;
}

export async function regeneratePersonaOpportunitySet(
  id: string,
  body: RegeneratePersonaOpportunitySetRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(id)}/regenerate`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!correlationId) {
    throw new Error("Regenerate response missing correlationId");
  }
  return { correlationId };
}
